import React from 'react';
import { Info } from 'lucide-react';
import { LegalDocument, PageHero } from '../components/page';
import SmartLink from '../components/SmartLink';

const EMAIL = 'support@groupmoney.app';

const MailLink = () => <a href={`mailto:${EMAIL}`}>{EMAIL}</a>;

// Highlighted note inside a legal section.
const Callout = ({ title, children }) => (
  <div className="!mt-6 flex gap-3 rounded-2xl border border-brand-400/20 bg-brand-400/[0.06] p-5">
    <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand-300" />
    <div>
      <p className="font-semibold text-white">{title}</p>
      <div className="mt-1 text-slate-300">{children}</div>
    </div>
  </div>
);

const TIERS = [
  { name: 'Free', price: '$0', detail: 'Limited features' },
  { name: 'Basic', price: '$5.99/mo', detail: 'or $59.90/year' },
  { name: 'Max', price: '$9.99/mo', detail: 'or $99.90/year' },
];

const TierSummary = () => (
  <div className="!mt-6 grid gap-3 sm:grid-cols-3">
    {TIERS.map((tier) => (
      <div key={tier.name} className="rounded-2xl border border-white/[0.06] bg-ink-900/80 p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{tier.name}</p>
        <p className="!mt-2 text-2xl font-bold tabular-nums tracking-tight text-white">{tier.price}</p>
        <p className="!mt-1 text-sm text-slate-400">{tier.detail}</p>
      </div>
    ))}
  </div>
);

const SECTIONS = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: (
      <>
        <p>
          By creating an account, joining a group, or using any part of Groupmoney (including features like "Group Rules," "Resolution Center," or upcoming Group Discounts), you accept these Terms and Conditions, our <SmartLink to="/privacy">Privacy Policy</SmartLink>, and any additional policies we may post. You must be at least 19 years old to use our services, in line with Canadian consent laws.
        </p>
        <Callout title="Important">
          By using Groupmoney, you confirm that you've read, understood, and agreed to be bound by these Terms, our Privacy Policy, and any other applicable agreements.
        </Callout>
      </>
    ),
  },
  {
    id: 'account',
    title: '2. Account Registration and Use',
    content: (
      <>
        <h3>Eligibility</h3>
        <p>
          You must provide accurate, complete, and current information when registering. You are responsible for maintaining the confidentiality of your account credentials.
        </p>
        <h3>Group Creation</h3>
        <p>You can create unlimited groups and accounts within each group, subject to these terms and applicable fees.</p>
        <h3>Member Invitations</h3>
        <p>You may invite others to join your group, but they must agree to these Terms and our Privacy Policy.</p>
        <h3>Prohibited Use</h3>
        <p>
          You may not use Groupmoney for illegal activities, fraud, or to harass others. We reserve the right to suspend or terminate accounts violating this policy.
        </p>
      </>
    ),
  },
  {
    id: 'services',
    title: '3. Services and Features',
    content: (
      <>
        <h3>Description</h3>
        <p>
          Groupmoney provides tools for pooling funds, tracking progress, setting customizable rules (e.g., "Group Controls," "Invite Power"), resolving conflicts via the "Resolution Center," and (once available) accessing Group Discounts through partners.
        </p>
        <h3>Availability</h3>
        <p>
          We strive to ensure uninterrupted service but are not liable for downtime due to maintenance, technical issues, or events beyond our control (e.g., internet outages).
        </p>
        <h3>Changes</h3>
        <p>
          We may update features or discontinue services (e.g., Group Discounts if partnerships end) with 30 days' notice via email or in-app notification.
        </p>
      </>
    ),
  },
  {
    id: 'payment',
    title: '4. Payment Terms',
    content: (
      <>
        <h3>Pricing</h3>
        <p>
          Services are offered in tiers: Free, Basic ($5.99/month or $59.90/year), and Max ($9.99/month or $99.90/year). See our Pricing Page for details. Transaction fees apply (e.g., $1 per withdrawal after 2 free in Free tier).
        </p>
        <TierSummary />
        <h3>Billing</h3>
        <p>
          Payments are processed via third-party providers (e.g., Stripe). You authorize us to charge your payment method for recurring subscriptions or fees.
        </p>
        <h3>Refunds</h3>
        <p>Subscriptions are non-refundable except as required by law. Transaction fees are non-refundable unless due to our error.</p>
        <h3>Cancellation</h3>
        <p>
          You may cancel your subscription anytime via your account settings, effective at the end of the billing cycle. You'll revert to the Free tier.
        </p>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: '5. Intellectual Property',
    content: (
      <>
        <p>
          All content on Groupmoney (e.g., design, logos, code) is owned by us or our licensors and protected by Canadian copyright and trademark laws. You may not reproduce, distribute, or modify it without permission.
        </p>
        <p>
          You retain ownership of data you provide (e.g., transaction details), but grant us a license to use it to provide and improve our services.
        </p>
      </>
    ),
  },
  {
    id: 'limitation',
    title: '6. Limitation of Liability',
    content: (
      <>
        <h3>No Warranty</h3>
        <p>
          Groupmoney is provided "as is" without warranties beyond what is legally required. We do not guarantee uninterrupted service or error-free performance.
        </p>
        <h3>Liability Cap</h3>
        <p>
          Our liability is limited to the amount you've paid us in the last 12 months, except where prohibited by Canadian consumer protection laws (e.g., Ontario's Consumer Protection Act).
        </p>
        <h3>Exclusions</h3>
        <p>
          We are not liable for indirect damages (e.g., lost profits) or issues caused by your misuse, third parties, or force majeure (e.g., natural disasters).
        </p>
      </>
    ),
  },
  {
    id: 'dispute',
    title: '7. Dispute Resolution',
    content: (
      <>
        <h3>Mediation</h3>
        <p>
          For group conflicts, use the "<SmartLink to="/resolution-center">Resolution Center</SmartLink>" (see <SmartLink to="/privacy">Privacy Policy</SmartLink> for details). We mediate neutrally and prioritize amicable solutions.
        </p>
        <h3>Legal Disputes</h3>
        <p>
          Any legal disputes arising from these Terms will be resolved under the laws of Ontario, Canada, and adjudicated in Ottawa courts. You agree to attempt mediation before pursuing litigation.
        </p>
        <h3>Class Actions</h3>
        <p>You waive the right to participate in class action lawsuits against Groupmoney, where permitted by law.</p>
      </>
    ),
  },
  {
    id: 'termination',
    title: '8. Termination',
    content: (
      <>
        <h3>By You</h3>
        <p>You may terminate your account anytime via settings, with data retained per our Privacy Policy (e.g., 2 years post-closure).</p>
        <h3>By Us</h3>
        <p>
          We may suspend or terminate your account for violating these Terms (e.g., fraud) with 7 days' notice, unless immediate action is required for safety or legality.
        </p>
        <h3>Effect</h3>
        <p>Termination stops access to paid features, but core data remains accessible under the Free tier or Privacy Policy retention rules.</p>
      </>
    ),
  },
  {
    id: 'indemnification',
    title: '9. Indemnification',
    content: (
      <p>
        You agree to indemnify and hold Groupmoney harmless from claims, damages, or losses arising from your use of the platform (e.g., disputes with group members), except where caused by our gross negligence.
      </p>
    ),
  },
  {
    id: 'governing-law',
    title: '10. Governing Law',
    content: (
      <p>
        These Terms are governed by the laws of Ontario, Canada, and any disputes will be resolved in accordance with Canadian legal standards, including PIPEDA and provincial consumer protection laws.
      </p>
    ),
  },
  {
    id: 'changes',
    title: '11. Changes to Terms',
    content: (
      <>
        <p>
          We may update these Terms to reflect changes in our services or legal requirements. We'll notify you via email or in-app at least 30 days before changes take effect. Your continued use after updates constitutes acceptance.
        </p>
        <h3>Terms Update History</h3>
        <div className="flex flex-col gap-1 rounded-2xl border border-white/[0.06] bg-ink-900/80 p-5 sm:flex-row sm:gap-6">
          <p className="shrink-0 font-semibold tabular-nums text-brand-300">March 13, 2025</p>
          <p className="!mt-0 text-slate-300">Initial Terms published</p>
        </div>
      </>
    ),
  },
  {
    id: 'contact-us',
    title: '12. Contact Us',
    content: (
      <>
        <p>For questions or concerns about these Terms, reach out:</p>
        <h3>Email</h3>
        <p>
          <MailLink />
        </p>
        <h3>Mail</h3>
        <p className="text-slate-300">
          Groupmoney
          <br />
          3-425 McArthur Ave.
          <br />
          Ottawa, ON, Canada
        </p>
        <h3>In-App</h3>
        <p>
          Log in, go to your profile, and visit the "<SmartLink to="/resolution-center">Resolution Center</SmartLink>" for support.
        </p>
        <h3>Regulatory Authorities</h3>
        <p>
          If unresolved, you may contact the Office of the Privacy Commissioner of Canada at 1-800-282-1376 or{' '}
          <SmartLink to="https://www.priv.gc.ca">www.priv.gc.ca</SmartLink>, or the Consumer Protection Ontario at 1-800-889-9768 or{' '}
          <SmartLink to="https://www.ontario.ca/page/consumer-protection-ontario">www.ontario.ca/page/consumer-protection-ontario</SmartLink>.
        </p>
        <p className="border-t border-white/[0.06] pt-6 text-sm text-slate-500">
          These Terms and Conditions were last updated on March 13, 2025 and are effective as of that date.
        </p>
      </>
    ),
  },
];

const TermsPage = () => (
  <>
    <PageHero
      eyebrow="Legal"
      meta="Last Updated: March 13, 2025"
      title="Terms and Conditions"
      description="Welcome to Groupmoney! These Terms and Conditions govern your use of our platform, which helps groups pool money for shared goals (e.g., trips, expenses, events). By accessing or using Groupmoney, you agree to be bound by these terms. If you do not agree, please do not use our services. This agreement is made under the laws of Canada and is subject to applicable provincial regulations."
    >
      <p className="text-base text-slate-500">
        For questions, contact us at{' '}
        <a href={`mailto:${EMAIL}`} className="font-medium text-brand-300 underline-offset-4 hover:underline">
          {EMAIL}
        </a>
        .
      </p>
    </PageHero>
    <LegalDocument sections={SECTIONS} />
  </>
);

export default TermsPage;
